import { useNavigate, useSearchParams } from 'react-router-dom';
import { FC, Fragment, useMemo, useReducer } from 'react';
import { Typography } from '@mui/material';
import { Box } from '@mui/system';
import { useTranslation } from 'react-i18next';
import { AxiosError } from 'axios';

import { ErrorMessage } from 'src/shared/enum/ErrorMessage';
import { ErrorReason } from 'src/shared/enum/BookingStatus';
import { useRebookerFlow } from 'src/utils/get-is-rebooker';
import { buildRoomsUrl } from 'src/utils/get-stl-rooms-url';

import { useFetchCalculateBookingDetails } from '../../lib/hooks/use-fetch-calculate-booking-details';
import { routes } from '../../constants/routes';
import { useGetBasketById } from '../../lib/hooks/use-get-basket-by-id';
import { BookingDetailsView } from '../../features/booking-summary/booking-details';
import { RoomCard } from '../../features/room-card';
import { Currency } from '../../shared/enum/Currency';
import { Term } from '../../shared/types/booking-types';
import { InstalmentType } from '../../shared/enum/InstalmentType';
import { useFetchCreatePaymentPlan } from '../../lib/hooks/use-create-payment-plan';
import { PaymentPlanView } from '../../features/payment-plan/payment-plan-view';
import { Button } from '../../components/button';
import { RoomCardSkeleton } from '../../features/room-card/room-card-skeleton';
import { BookingDetailsSkeleton } from '../../features/booking-summary/booking-details/booking-details-skeleton';
import { useHandleQueryError } from '../../lib/hooks/use-handle-query-error';
import { useUrlParams } from '../../utils/use-url-params';
import { getAgentUserSearchParams } from '../../utils/get-agent-user-search-params';
import {
    getAgentCode,
    getAgentStudentId,
    setRedirectStateFromParams,
} from '../../utils/local-storage-versioning';

import { layoutStyles, styles } from './styles';
import { bookingDetailsReducer } from './booking-details-reducer';
import { PaymentPlanReducerTypes } from './types';
import { usePostSubmitBasket } from './hooks/post-submit-basket';

export const BookingSummaryContainer: FC = () => {
    const { t } = useTranslation();
    const [params] = useSearchParams();
    const urlParams = useUrlParams();

    const [userChoices, dispatchUserChoices] = useReducer(bookingDetailsReducer, {
        numberOfStudents: 1,
        term: null,
        instalmentType: null,
        instalmentId: null,
    });
    const paymentPlanChoices = { ...userChoices, term: userChoices.term?.name ?? null };

    const navigate = useNavigate();

    const roomCode = params.get('roomCode') ?? '';
    const academicYear = params.get('academicYear') ?? '';
    const pid = (params.get('pid') || params.get('promotionId')) ?? null;
    const paymentPlanId = params.get('paymentPlanId') ?? null;
    const cbl = params.get('cbl') ?? null;
    const isRebooker = params.get('rebooker') === 'true';

    const { isValidRebooker } = useRebookerFlow(roomCode, isRebooker);
    const agentCode = getAgentCode(params);
    const agentStudentId = getAgentStudentId(params);
    const isOriaRoom = roomCode.includes('OR1');

    let promotionId = pid;

    if (isValidRebooker?.promotion_id) {
        promotionId = isValidRebooker.promotion_id;
    }

    const {
        data: calculatedBookingDetails,
        isError: failedToCalculateBookingDetails,
        error: calculateBookingDetailsError,
        isLoading: isCalculatingBookingDetails,
        refetch: refetchBookingDetails,
    } = useFetchCalculateBookingDetails(roomCode, academicYear, paymentPlanId);

    const {
        data: basket,
        isError: failedToGetBasket,
        isLoading: isFetchingBasket,
    } = useGetBasketById();

    const {
        data: createdPaymentPlan,
        isFetching: fetchingPaymentPlan,
        isError: failedToCreatePaymentPlan,
    } = useFetchCreatePaymentPlan({
        userChoices: paymentPlanChoices,
        promotionId,
        isRebooker,
        fillerWeekEligible: false,
        roomData: calculatedBookingDetails?.room ?? null,
        bookingId: null,
    });

    const {
        isPending: isSubmittingBasket,
        mutate: submitBasket,
        error: updateBasketError,
        isError: failedToUpdateBasket,
        reset: resetSubmitBasket,
    } = usePostSubmitBasket();

    const handleQueryError = useHandleQueryError();

    useMemo(() => {
        if (failedToUpdateBasket && updateBasketError) {
            // The stored basket still holds the previous room when this update fails, so link back
            // to the search for the room that was being booked instead
            const { room } = calculatedBookingDetails ?? {};
            const roomsUrl =
                paymentPlanId && room
                    ? buildRoomsUrl(
                          room.development_slug,
                          room.academic_year,
                          userChoices.term ?? undefined
                      )
                    : null;

            handleQueryError(updateBasketError, academicYear, true, roomsUrl);
            if (
                updateBasketError instanceof AxiosError &&
                updateBasketError.response?.data?.data?.room?.[0]
            ) {
                resetSubmitBasket();
                refetchBookingDetails();
            }
        }
    }, [updateBasketError]);

    if (!roomCode || !academicYear) {
        navigate(routes.noRoomSelected.index);
        return null;
    }

    if (calculateBookingDetailsError?.message === ErrorMessage.RESERVATION_EXPIRED) {
        navigate(
            `${routes.failure.index}?reason=${encodeURIComponent(ErrorReason.RESERVATION_EXPIRED)}`
        );
        return null;
    }

    if (failedToGetBasket || failedToCalculateBookingDetails) {
        navigate(routes.failure.index);
        return null;
    }

    const handleTermChange = (value: Term) => {
        dispatchUserChoices({ type: PaymentPlanReducerTypes.SET_TERM, data: value });
        const selectedInstalment = value.instalments.find(
            (instalment) => instalment.name === userChoices.instalmentType
        );

        const instalmentName = selectedInstalment
            ? selectedInstalment.name
            : value.instalments[0].name;
        const instalmentId = selectedInstalment ? selectedInstalment.id : value.instalments[0].id;

        dispatchUserChoices({
            type: PaymentPlanReducerTypes.SET_INSTALMENT_TYPE,
            data: instalmentName as InstalmentType,
        });
        dispatchUserChoices({
            type: PaymentPlanReducerTypes.SET_INSTALMENT_ID,
            data: instalmentId,
        });
    };

    const handleInstalmentChange = (value: string | null, id: string | null) => {
        dispatchUserChoices({
            type: PaymentPlanReducerTypes.SET_INSTALMENT_TYPE,
            data: value as InstalmentType,
        });
        dispatchUserChoices({ type: PaymentPlanReducerTypes.SET_INSTALMENT_ID, data: id });
    };

    const incrementStudentNumber = () => {
        if (!calculatedBookingDetails) return;

        if (
            userChoices.numberOfStudents &&
            userChoices.numberOfStudents <
                calculatedBookingDetails.bookingDetails.options.students.maxStudents
        ) {
            const updatedNumberOfStudents = userChoices.numberOfStudents
                ? userChoices.numberOfStudents + 1
                : 1;
            dispatchUserChoices({
                type: PaymentPlanReducerTypes.SET_NUMBER_OF_STUDENTS,
                data: updatedNumberOfStudents,
            });
        }
    };

    const decrementStudentNumber = () => {
        if (!calculatedBookingDetails) return;

        if (
            userChoices.numberOfStudents &&
            userChoices.numberOfStudents >
                calculatedBookingDetails.bookingDetails.options.students.minStudents
        ) {
            const updatedNumberOfStudents = userChoices.numberOfStudents
                ? userChoices.numberOfStudents - 1
                : 1;
            dispatchUserChoices({
                type: PaymentPlanReducerTypes.SET_NUMBER_OF_STUDENTS,
                data: updatedNumberOfStudents,
            });
        }
    };

    const setNumberOfStudents = (students: number) =>
        dispatchUserChoices({
            type: PaymentPlanReducerTypes.SET_NUMBER_OF_STUDENTS,
            data: students,
        });

    const handleSubmit = async () => {
        if (!createdPaymentPlan) return;
        if (!calculatedBookingDetails) return;

        setRedirectStateFromParams(params);

        submitBasket(
            {
                room: calculatedBookingDetails.room,
                paymentPlanId,
                userChoices,
                paymentPlan: createdPaymentPlan,
                basket,
                isValidRebooker,
                agentCode,
                agentStudentId,
                cbl: cbl === 'true',
                paymentOptions: calculatedBookingDetails.bookingDetails.terms,
            },
            {
                onSuccess() {
                    const agentSearchParams = getAgentUserSearchParams(params, urlParams);

                    if (!agentSearchParams) {
                        navigate(routes.yourDetails.index);
                        return;
                    }

                    navigate({
                        pathname: routes.yourDetails.index,
                        search: `?${agentSearchParams.toString()}`,
                    });
                },
            }
        );
    };

    const isLoading = isCalculatingBookingDetails || isFetchingBasket;
    const hasDiscount = Boolean(createdPaymentPlan?.promotion);

    return (
        <Box>
            <Box css={layoutStyles(hasDiscount)}>
                <Box css={styles.wrapper}>
                    <Typography variant="h1" css={styles.title}>
                        {t('booking_summary_title')}
                    </Typography>

                    {isLoading || !calculatedBookingDetails ? (
                        <Fragment>
                            <RoomCardSkeleton />
                            <BookingDetailsSkeleton />
                        </Fragment>
                    ) : (
                        <Fragment>
                            <RoomCard
                                hasDiscount={hasDiscount}
                                roomData={calculatedBookingDetails.room}
                                building={calculatedBookingDetails.building}
                                termDuration={userChoices.term?.duration ?? 0}
                                termStartDate={userChoices.term?.startDate ?? ''}
                                termEndDate={userChoices.term?.endDate ?? ''}
                                numberOfStudents={userChoices.numberOfStudents}
                            />
                            <BookingDetailsView
                                setNumberOfStudents={setNumberOfStudents}
                                handleTermChange={handleTermChange}
                                handleInstalmentChange={handleInstalmentChange}
                                incrementStudentNumber={incrementStudentNumber}
                                decrementStudentNumber={decrementStudentNumber}
                                roomData={calculatedBookingDetails?.room}
                                bookingDetails={calculatedBookingDetails?.bookingDetails}
                                userChoices={userChoices}
                                loading={isLoading}
                            />
                        </Fragment>
                    )}
                    <PaymentPlanView
                        createdPaymentPlan={createdPaymentPlan}
                        loading={fetchingPaymentPlan}
                        currency={calculatedBookingDetails?.room?.currency as Currency}
                        showSantanderCards={isOriaRoom}
                        id="booking_summary_payment_plan"
                    />

                    <Button
                        variant="contained"
                        css={styles.cta}
                        onClick={handleSubmit}
                        loading={fetchingPaymentPlan || isSubmittingBasket}
                        disabled={failedToCreatePaymentPlan}
                        id="booking-summary-submit-button"
                    >
                        {t('booking_summary_payment_cta')}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
};
